import * as React from 'react';
//import {Card, Box, Typography, CardMedia, Grid, Stack, IconButton} from '@mui/material';
import {
  Card,
  CardActions,
  CardContent,
  CardMedia,
  Typography,
  Box,
  IconButton,
  Stack,
  CardActionArea,
  styled,
  Tooltip,
  TooltipProps,
  tooltipClasses,
  Grid,
  useTheme,
} from '@mui/material';
import {Pin, PinFilled, Link} from '@carbon/icons-react';
import {
  CardType,
  ITrainingCard,
  IDocumentCard,
  IProcessCard,
  IArtifact,
  PinTypes,
  newDocumentCategories,
} from 'app/types/Shared';
import GoverningStandardProcessIcon from 'assets/images/ArtifactIcons/GoverningStandardProcess.svg';
import KeyProcessGxPIcon from 'assets/images/ArtifactIcons/keyProcessGxP.svg';
import KeyProcessBusinessControlledIcon from 'assets/images/ArtifactIcons/keyProcessBusinessControlled.svg';
import VeevaIcon from 'assets/images/ArtifactIcons/VeevaVault.svg';
import BloomIcon from 'assets/images/ArtifactIcons/Bloom.svg';
import ProcessGear from 'assets/images/ArtifactIcons/ProcessGear.svg';
import SharepointIcon from 'assets/images/ArtifactIcons/Sharepoint.svg';
import BlankDoc from 'assets/images/ArtifactIcons/BlankDoc.svg';
import {useSelector} from 'react-redux';
import {selectProcessesWithMultipleModels} from 'app/store/operatingModel/operatingModelSlice';
import {TrackEventParams} from '@jonkoops/matomo-tracker-react/lib/types';

// Used to display Training videos on cards -- Required Params: Type= 'video', Title (string), duration (number)
type trainingProps = {
  training: ITrainingCard;
  pinned: boolean;
  pinsCallback?: (type: string, action: boolean, artifact: IArtifact) => void;
  setRecentlyViewed?: (artifact: IArtifact) => void;
  copy?: (link: string) => void;
};

const Training = ({training, pinned, pinsCallback, copy, setRecentlyViewed}: trainingProps) => {
  const [pin, setPin] = React.useState(pinned);
  const theme = useTheme();

  React.useEffect(() => {
    setPin(pinned);
  }, [pinned]);

  const handleCopy = () => {
    if (copy && training.url) {
      copy(training.url);
    }
  };

  const getCardIcon = (source: string) => {
    const sourceToLower = source?.toLowerCase();

    if (sourceToLower?.includes('veeva')) {
      return VeevaIcon;
    } else if (sourceToLower?.includes('bloom')) {
      return BloomIcon;
    } else if (sourceToLower?.includes('sharepoint')) {
      return SharepointIcon;
    } else {
      return BlankDoc;
    }
  };

  const handleClick = () => {
    if (pin) {
      setPin(false);
      if (pinsCallback) {
        pinsCallback(PinTypes.Training, false, {
          id: training.id,
          title: training.title,
          url: training.url,
          type: 'training',
          created_dt: '9999-99-99',
          key: training.key,
        });
      }
    } else {
      setPin(true);
      if (pinsCallback) {
        pinsCallback(PinTypes.Training, true, {
          id: training.id,
          title: training.title,
          url: training.url,
          type: 'training',
          created_dt: '9999-99-99',
          key: training.key,
        });
      }
    }
  };

  const handleOpen = () => {
    if (training.url) {
      window.open(training.url);
    }
    if (setRecentlyViewed) {
      setRecentlyViewed({
        id: training.id,
        title: training.title,
        key: training.key,
        url: training.url,
        type: training.type ? training.type : 'training',
        created_dt: training.created_dt ? training.created_dt : '0000-00-00',
        docid: training.key,
      });
    }
  };

  return (
    <Card
      // variant='outlined'
      sx={{
        display: 'flex',
        direction: 'row',
        minHeight: 72,
        maxHeight: 72,
        minWidth: '330px',
        justifyContent: 'flex-start',
        maxWidth: '330px',
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
      }}
    >
      <CardActionArea sx={{display: 'flex', alignItems: 'flex-start', py: '10px'}} onClick={() => handleOpen()}>
        <CardMedia
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: '20px',
          }}
        >
          <img
            width={32}
            height={32}
            src={getCardIcon(training.source ? training.source : '')}
            alt={training.source ? training.source : ''}
          />
        </CardMedia>
        <CardContent
          sx={{
            flexGrow: 1,
            height: '100%',
            justifyContent: 'flex-start',
            alignItems: 'center',
            display: 'flex',
            px: 0,
          }}
        >
          <Box
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              textOverflow: 'ellipsis',
            }}
          >
            <Typography variant='subtitle1' lineHeight='135%'>
              {training.title}
            </Typography>
            <Typography variant='subtitle1' lineHeight='135%'>
              {training.level_label}
            </Typography>
            {/* <Typography variant='subtitle1'>
            {document.eff_date ? document.eff_date : document.created_dt.split(' ')}
          </Typography> */}
          </Box>
        </CardContent>
      </CardActionArea>
      <CardActions
        disableSpacing
        sx={{display: 'flex', justifyContent: 'space-between', flexDirection: 'column', p: '10px'}}
      >
        <IconButton color='primary' sx={{p: 0, pt: 0.2}} onClick={() => handleCopy()} disableRipple>
          <Box sx={{color: theme.palette.grey[700], '&:hover': {color: theme.palette.secondary.main}}}>
            <Link color='inherit' height='20px' width='20px' />
          </Box>
        </IconButton>
        <IconButton color='primary' onClick={() => handleClick()} sx={{p: 0, pb: 0.2}} disableRipple>
          {pin ? (
            <Box>
              <PinFilled fill={theme.palette.primary.main} height='20px' width='20px' />
            </Box>
          ) : (
            <Box sx={{color: theme.palette.grey[700], '&:hover': {color: theme.palette.secondary.main}}}>
              <Pin color='inherit' height='20px' width='20px' />
            </Box>
          )}
        </IconButton>
      </CardActions>
    </Card>
  );
};

type documentProps = {
  document: IDocumentCard;
  pinned: boolean;
  pinsCallback?: (type: string, action: boolean, artifact: IArtifact) => void;
  setRecentlyViewed?: (artifact: IArtifact) => void;
  copy?: (link: string) => void;
  trackClick?: (params: TrackEventParams) => void | undefined;
};
// Used to display Documents on cards -- Required Params: Type= 'document', Title (string), created (string)
const Document = ({document, pinned, pinsCallback, setRecentlyViewed, copy, trackClick}: documentProps) => {
  const [pin, setPin] = React.useState(pinned);
  const theme = useTheme();

  React.useEffect(() => {
    setPin(pinned);
  }, [pinned]);

  const handleCopy = () => {
    if (copy && document.url) {
      copy(document.url);
    }
  };

  const handleClick = () => {
    if (pin) {
      setPin(false);
      if (pinsCallback) {
        pinsCallback(PinTypes.Artifact, false, {
          id: document.id,
          title: document.title,
          url: document.url,
          key: document.key,
          type: document.type,
          created_dt: document.created_dt,
        });
      }
    } else {
      setPin(true);
      if (pinsCallback) {
        pinsCallback(PinTypes.Artifact, true, {
          id: document.id,
          title: document.title,
          key: document.key,
          url: document.url,
          type: document.type,
          created_dt: document.created_dt,
        });
      }
    }
  };

  const handleOpen = () => {
    if (document.url) {
      trackClick && trackClick({category: 'Document', action: 'Opened', name: document.type});
      window.open(document.url);
    }

    if (setRecentlyViewed) {
      setRecentlyViewed({
        id: document.id,
        title: document.title,
        key: document.key,
        url: document.url,
        type: document.type,
        created_dt: document.created_dt,
        docid: document.docid,
        system: document.system,
        eff_date: document.eff_date,
        owner: document.owner,
        owner_email: document.owner_email,
        j_process_artifact: document.j_process_artifact,
      });
    }
  };

  const getCardIcon = (category: string) => {
    if (category === newDocumentCategories.GoverningStandardProcess) {
      return GoverningStandardProcessIcon;
    } else if (category === newDocumentCategories.KeyProcessBusinessControlled) {
      return KeyProcessBusinessControlledIcon;
    } else if (category === newDocumentCategories.KeyProcessGxP) {
      return KeyProcessGxPIcon;
    } else {
      return null;
    }
  };

  const hasCardIcon = () => {
    const category = getDocumentCategoryByProcess();
    return (
      category === newDocumentCategories.GoverningStandardProcess ||
      category === newDocumentCategories.KeyProcessBusinessControlled ||
      category === newDocumentCategories.KeyProcessGxP
    );
  };

  const ArtifactCardToolTip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.grey[700],
      maxWidth: '280px',
      minWidth: '250px',
    },
  });

  const getDocumentCategoryByProcess = () => {
    if (document.j_process_artifact?.length) {
      const artifact = document.j_process_artifact.find(
        ({process: {process_id}}) => process_id === document.parent_process_id,
      );
      return artifact?.document_category || '';
    }
    return '';
  };

  return (
    <Card
      // variant='outlined'
      sx={{
        display: 'flex',
        direction: 'row',
        minHeight: 72,
        maxHeight: 72,
        minWidth: '330px',
        justifyContent: 'flex-start',
        maxWidth: '330px',
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
      }}
    >
      <CardActionArea sx={{display: 'flex', alignItems: 'flex-start', py: '13px'}} onClick={() => handleOpen()}>
        <ArtifactCardToolTip
          title={
            <Box p={0.2}>
              <Stack direction='column' alignItems='flex-start' justifyContent='flex-start'>
                <Typography>{document.title}</Typography>
                <Box px={4} py={0.2} borderBottom='0.5px solid white' width='100%' />
                <Grid container rowSpacing={1} padding={0} margin={0}>
                  <Grid item xs={3}>
                    <Typography>Type:</Typography>
                  </Grid>
                  <Grid item xs={9}>
                    <Typography variant='h6'>{document.type}</Typography>
                  </Grid>
                  {document.system && (
                    <Grid item xs={3}>
                      <Typography>System:</Typography>
                    </Grid>
                  )}
                  {document.system && (
                    <Grid item xs={9}>
                      <Typography variant='h6'>{document.system}</Typography>
                    </Grid>
                  )}
                  {document.owner && document.owner_email && (
                    <Grid item xs={3}>
                      <Typography>Owner:</Typography>
                    </Grid>
                  )}
                  {document.owner && document.owner_email && (
                    <Grid item xs={9}>
                      <Typography variant='h6'>{document.owner}</Typography>
                    </Grid>
                  )}
                  {document.owner && document.owner_email && <Grid item xs={3} />}
                  {document.owner && document.owner_email && (
                    <Grid item xs={9}>
                      <Typography variant='h6'>{document.owner_email}</Typography>
                    </Grid>
                  )}
                </Grid>
              </Stack>
            </Box>
          }
          placement='right'
        >
          <CardMedia
            sx={{
              height: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              px: '13px',
            }}
          >
            {hasCardIcon() ? (
              <img
                width={46}
                height={46}
                src={getCardIcon(getDocumentCategoryByProcess())}
                alt={getDocumentCategoryByProcess()}
              />
            ) : (
              <Box width={46} height={46} zIndex={1} />
            )}
          </CardMedia>
        </ArtifactCardToolTip>
        <CardContent
          sx={{
            flexGrow: 1,
            height: '100%',
            justifyContent: 'flex-start',
            alignItems: 'center',
            display: 'flex',
            px: 0,
            ml: hasCardIcon() ? 'auto' : -6.5,
          }}
        >
          <Box
            sx={{
              display: '-webkit-box',
              overflow: 'hidden',
              WebkitBoxOrient: 'vertical',
              WebkitLineClamp: 3,
              textOverflow: 'ellipsis',
            }}
          >
            <Typography variant='subtitle1' lineHeight='135%'>
              {document.title}
            </Typography>
            {/* <Typography variant='subtitle1'>
            {document.eff_date ? document.eff_date : document.created_dt.split(' ')}
          </Typography> */}
          </Box>
        </CardContent>
      </CardActionArea>
      <CardActions
        disableSpacing
        sx={{display: 'flex', justifyContent: 'space-between', flexDirection: 'column', p: '10px'}}
      >
        <IconButton color='primary' sx={{p: 0, pt: 0.2}} onClick={() => handleCopy()} disableRipple>
          <Box sx={{color: theme.palette.grey[700], '&:hover': {color: theme.palette.secondary.main}}}>
            <Link color='inherit' height='20px' width='20px' />
          </Box>
        </IconButton>
        <IconButton color='primary' onClick={() => handleClick()} sx={{p: 0, pb: 0.2}} disableRipple>
          {pin ? (
            <Box>
              <PinFilled fill={theme.palette.primary.main} height='20px' width='20px' />
            </Box>
          ) : (
            <Box sx={{color: theme.palette.grey[700], '&:hover': {color: theme.palette.secondary.main}}}>
              <Pin color='inherit' height='20px' width='20px' />
            </Box>
          )}
        </IconButton>
      </CardActions>
    </Card>
  );
};

type processProps = {
  process: IProcessCard;
  pinned: boolean;
  pinsCallback?: (type: string, action: boolean, artifact: IArtifact) => void;
  setRecentlyViewed?: (artifact: IArtifact) => void;
  linkTo?: (title: string, url: string) => Promise<void>;
  copy?: (link: string) => void;
  disabled: boolean;
};

// Used to display processes on cards -- Required Params: Type= 'process', Title (string)
const Process = ({process, pinned, pinsCallback, setRecentlyViewed, linkTo, disabled}: processProps) => {
  const [pin, setPin] = React.useState(pinned);
  const theme = useTheme();
  const processWithMultipleModels = useSelector(selectProcessesWithMultipleModels);

  const ProcessCardToolTip = styled(({className, ...props}: TooltipProps) => (
    <Tooltip {...props} classes={{popper: className}} />
  ))({
    [`& .${tooltipClasses.tooltip}`]: {
      backgroundColor: theme.palette.grey[700],
      maxWidth: 'none',
    },
  });

  React.useEffect(() => {
    setPin(pinned);
  }, [pinned]);

  const handleClick = () => {
    if (pin) {
      setPin(false);
      if (pinsCallback) {
        pinsCallback(PinTypes.Process, false, {
          id: process.id,
          title: process.title,
          url: process.url,
          type: process.type,
          created_dt: process.created_dt,
          key: process.key,
        });
      }
    } else {
      setPin(true);
      if (pinsCallback) {
        pinsCallback(PinTypes.Process, true, {
          id: process.id,
          title: process.title,
          url: process.url,
          type: process.type,
          created_dt: process.created_dt,
          key: process.key,
        });
      }
    }
  };

  const handleOpen = () => {
    if (process.title && linkTo && process.url) linkTo(process.title, process.url);
    if (setRecentlyViewed) {
      setRecentlyViewed({
        id: process.id,
        title: process.title,
        url: process.url,
        type: process.type,
        created_dt: process.created_dt,
        key: process.key,
      });
    }
  };

  const getModel = () => {
    return processWithMultipleModels?.find((p) => p.process_id === process.key)?.model;
  };

  return (
    <Card
      // variant='outlined'
      sx={{
        display: 'flex',
        direction: 'row',
        minHeight: 72,
        maxHeight: 72,
        minWidth: '330px',
        justifyContent: 'flex-start',
        maxWidth: '330px',
        boxShadow: '0px 2px 4px 0px rgba(0, 0, 0, 0.1)',
      }}
    >
      <CardActionArea
        sx={{display: 'flex', alignItems: 'flex-start', py: '10px'}}
        onClick={() => handleOpen()}
        disabled={disabled}
      >
        <CardMedia
          sx={{
            height: '100%',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            px: '20px',
          }}
        >
          <img width={32} height={32} src={ProcessGear} alt={'process card'} />
        </CardMedia>
        <ProcessCardToolTip
          title={
            <Box sx={{px: 0.2}}>
              <Stack direction='row' spacing={0.5}>
                <Typography>{getModel() && `(${getModel()}) `}</Typography>
                <Typography>{process.title}</Typography>
              </Stack>
            </Box>
          }
          placement='bottom'
        >
          <CardContent
            sx={{
              flexGrow: 1,
              height: '100%',
              justifyContent: 'flex-start',
              alignItems: 'center',
              display: 'flex',
              px: 0,
            }}
          >
            <Box
              sx={{
                display: '-webkit-box',
                overflow: 'hidden',
                WebkitBoxOrient: 'vertical',
                WebkitLineClamp: 3,
                textOverflow: 'ellipsis',
              }}
            >
              <Typography variant='subtitle1' lineHeight='135%'>
                {getModel() && `(${getModel()}) `}
                {process.title}
              </Typography>
            </Box>
          </CardContent>
        </ProcessCardToolTip>
      </CardActionArea>
      <CardActions disableSpacing sx={{display: 'flex', justifyContent: 'center', flexDirection: 'column', p: '10px'}}>
        <IconButton color='primary' onClick={() => handleClick()} sx={{p: 0, pb: 0.2}} disableRipple>
          {pin ? (
            <Box>
              <PinFilled fill={theme.palette.primary.main} height='20px' width='20px' />
            </Box>
          ) : (
            <Box sx={{color: theme.palette.grey[700], '&:hover': {color: theme.palette.secondary.main}}}>
              <Pin color='inherit' height='20px' width='20px' />
            </Box>
          )}
        </IconButton>
      </CardActions>
    </Card>
  );
};

type CardProps = {
  data: ITrainingCard | IDocumentCard | IProcessCard;
  pinned: boolean;
  pinsCallback?: (type: string, action: boolean, artifact: IArtifact) => void;
  setRecentlyViewed?: (artifact: IArtifact) => void;
  linkTo?: (title: string, url: string) => Promise<void>;
  copy?: (link: string) => void;
  disabled?: boolean;
  trackClick?: (params: TrackEventParams) => void | undefined;
};
//Control fucntion: Renders correct card based on parameters passed in
export const ArtifactCard: React.FC<CardProps> = ({
  data,
  pinned,
  pinsCallback,
  setRecentlyViewed,
  linkTo,
  copy,
  disabled = false,
  trackClick,
}: CardProps) => {
  return (
    <>
      {data.card == CardType.process && (
        <Process
          process={data}
          pinned={pinned}
          pinsCallback={pinsCallback}
          setRecentlyViewed={setRecentlyViewed}
          linkTo={linkTo}
          copy={copy}
          disabled={disabled}
        />
      )}
      {data.card == CardType.training && (
        <Training
          training={data}
          pinned={pinned}
          pinsCallback={pinsCallback}
          copy={copy}
          setRecentlyViewed={setRecentlyViewed}
        />
      )}
      {data.card == CardType.document && (
        <Document
          document={data}
          pinned={pinned}
          pinsCallback={pinsCallback}
          setRecentlyViewed={setRecentlyViewed}
          copy={copy}
          trackClick={trackClick}
        />
      )}
    </>
  );
};
